import React from 'react';
import { Paperclip, Download, Trash2, ZoomIn } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { adminApi } from '@/lib/api';
import { getFileIcon, formatFileSize, isImageFile, formatDate } from './helpers';

export const AttachmentsCard = ({
  report, uploadingFile, fileInputRef,
  onFileUpload, onDownload, onDelete, onPreviewClick
}) => {
  const images = report.attachments?.filter(a => isImageFile(a.content_type)) || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Paperclip className="w-5 h-5" />
          Anexos / Evidências
        </CardTitle>
        <CardDescription>Ficheiros anexados à denúncia</CardDescription>
      </CardHeader>
      <CardContent>
        {images.length > 0 && (
          <div className="mb-4">
            <Label className="text-sm text-slate-500 mb-2 block">Imagens</Label>
            <div className="grid grid-cols-3 gap-2">
              {images.map((attachment) => (
                <div
                  key={attachment.id}
                  className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 cursor-pointer group"
                  onClick={() => onPreviewClick(attachment)}
                >
                  <img
                    src={adminApi.previewAttachment(attachment.id)}
                    alt={attachment.original_filename}
                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                    <ZoomIn className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {report.attachments && report.attachments.length > 0 ? (
          <div className="space-y-2 mb-4">
            <Label className="text-sm text-slate-500 mb-2 block">Todos os ficheiros ({report.attachments.length})</Label>
            {report.attachments.map((attachment) => (
              <div
                key={attachment.id}
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  {isImageFile(attachment.content_type) ? (
                    <div
                      className="w-10 h-10 rounded overflow-hidden cursor-pointer"
                      onClick={() => onPreviewClick(attachment)}
                    >
                      <img
                        src={adminApi.previewAttachment(attachment.id)}
                        alt={attachment.original_filename}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    getFileIcon(attachment.content_type)
                  )}
                  <div>
                    <p className="text-sm font-medium text-slate-900">{attachment.original_filename}</p>
                    <p className="text-xs text-slate-500">
                      {formatFileSize(attachment.size)} • {attachment.uploaded_by === 'reporter' ? 'Denunciante' : 'Gestor'} • {formatDate(attachment.created_at)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isImageFile(attachment.content_type) && (
                    <Button variant="ghost" size="sm" onClick={() => onPreviewClick(attachment)}>
                      <ZoomIn className="w-4 h-4" />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => onDownload(attachment.id, attachment.original_filename)}>
                    <Download className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(attachment.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-slate-500 py-4 mb-4">
            Nenhum anexo
          </p>
        )}

        <Separator className="my-4" />

        <div>
          <input
            ref={fileInputRef}
            type="file"
            onChange={onFileUpload}
            className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt,.csv,.mp3,.mp4,.wav,.zip"
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingFile || (report.attachments?.length >= 10)}
            className="w-full"
            data-testid="admin-upload-attachment-btn"
          >
            <Paperclip className="w-4 h-4 mr-2" />
            {uploadingFile ? 'A enviar...' : 'Adicionar Anexo'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
